import jsYaml from 'js-yaml';
import { deriveVerbsFromRequests, VALID_REQUEST_TYPES, NODE_FILTER_TYPES, REQUEST_TYPE_REGISTRY, requestTypeOf } from '../constants/formA';
const VALID_SOURCES = ['schema', 'any'];
const VALID_PARAM_TYPES = ['text', 'number', 'select', 'boolean', 'file', 'nodes', 'tag', 'schema', 'timestamp'];

/**
 * Parse a YAML DLR string and return structured dlrInputs + inputParameters,
 * or a list of validation errors.
 *
 * @param {string} yamlStr - Raw YAML string
 * @returns {{ dlrInputs: Array, inputParameters: Array, errors: string[] }}
 */
const parseDLRYaml = (yamlStr) => {
  const errors = [];
  let parsed;

  try {
    parsed = jsYaml.load(yamlStr);
  } catch (e) {
    return { dlrInputs: [], inputParameters: [], errors: [`YAML syntax error: ${e.message}`] };
  }

  if (!parsed || typeof parsed !== 'object') {
    return { dlrInputs: [], inputParameters: [], errors: ['YAML must contain a valid object with a "requests" array.'] };
  }

  if (!Array.isArray(parsed.requests)) {
    return { dlrInputs: [], inputParameters: [], errors: ['Missing or invalid "requests" array at top level.'] };
  }

  const dlrInputs = [];

  parsed.requests.forEach((req, i) => {
    const prefix = `Request #${i + 1}`;

    if (!req || typeof req !== 'object') {
      errors.push(`${prefix}: must be an object.`);
      return;
    }

    // Resolve the canonical registry key, accepting either a canonical `type:` (resource:verb)
    // or a legacy clause name (routed through requestTypeOf's fallback map).
    const type = requestTypeOf({ type: req.type });
    if (!type || !VALID_REQUEST_TYPES.includes(type)) {
      errors.push(`${prefix}: invalid type '${req.type || '(missing)'}'. Valid types: ${VALID_REQUEST_TYPES.join(', ')}`);
      return;
    }

    if (!req.key || typeof req.key !== 'string' || !req.key.trim()) {
      errors.push(`${prefix}: "key" is required and must be a non-empty string.`);
      return;
    }

    const item = {
      key: req.key.trim(),
      request_type: type,
    };

    if (req.label) item.label = String(req.label);
    if (req.description) item.description = String(req.description);

    // Node-filter type validation
    if (NODE_FILTER_TYPES.includes(type)) {
      const src = req.source || 'schema';
      if (!VALID_SOURCES.includes(src)) {
        errors.push(`${prefix}: invalid source '${src}'. Valid: ${VALID_SOURCES.join(', ')}`);
      }
      item.source = src;
      if (src === 'schema' && req.schemas) {
        item.schema_ids = Array.isArray(req.schemas) ? req.schemas : [req.schemas];
      } else if (src === 'schema' && req.schema) {
        item.schema_ids = [req.schema];
      }

      if (type !== 'nodes:create') {
        if (req.min !== undefined) {
          const minVal = Number(req.min);
          if (isNaN(minVal) || minVal < 0) {
            errors.push(`${prefix}: "min" must be a non-negative number.`);
          } else {
            item.min = minVal;
          }
        } else {
          item.min = 0;
        }

        if (req.max !== undefined) {
          if (req.max === 'unlimited') {
            item.max = null;
          } else {
            const maxVal = Number(req.max);
            if (isNaN(maxVal) || maxVal < 0) {
              errors.push(`${prefix}: "max" must be a non-negative number or "unlimited".`);
            } else {
              item.max = maxVal;
            }
          }
        } else {
          item.max = null;
        }
      }

      if (req.required !== undefined) {
        if (typeof req.required !== 'boolean') {
          errors.push(`${prefix}: "required" must be true or false.`);
        } else {
          item.required = req.required;
        }
      }
    }

    // tags:read — app declares it needs tag access; user decides scope during auth
    if (type === 'tags:read') {
      item.tag_visibility = 'some'; // always 'some' — user selects during auth
    }

    // plugins:execute
    if (type === 'plugins:execute') {
      if (Array.isArray(req.plugins)) {
        item.plugin_ids = req.plugins;
      } else {
        item.plugin_ids = [];
      }
    }

    // discovery
    if (type === 'discovery') {
      item.friends = req.friends !== undefined ? Boolean(req.friends) : true;
      item.groups = req.groups !== undefined ? Boolean(req.groups) : true;
      item.realms = req.realms !== undefined ? Boolean(req.realms) : false;
    }

    dlrInputs.push(item);
  });

  const inputParameters = [];
  const rawVars = parsed.variables;
  if (rawVars && Array.isArray(rawVars)) {
    rawVars.forEach((param, i) => {
      const prefix = `Variable #${i + 1}`;

      if (!param || typeof param !== 'object') {
        errors.push(`${prefix}: must be an object.`);
        return;
      }

      if (!param.key || typeof param.key !== 'string' || !param.key.trim()) {
        errors.push(`${prefix}: "key" is required and must be a non-empty string.`);
        return;
      }

      const pType = param.type || 'text';
      if (!VALID_PARAM_TYPES.includes(pType)) {
        errors.push(`${prefix}: invalid type '${pType}'. Valid: ${VALID_PARAM_TYPES.join(', ')}`);
        return;
      }

      const p = { key: param.key.trim(), type: pType };
      if (param.label) p.label = String(param.label);
      if (param.required !== undefined) p.required = Boolean(param.required);
      if (pType === 'select' && Array.isArray(param.options)) p.options = param.options.map(String);
      if (pType === 'file' && param.accept) p.accept = String(param.accept);

      // nodes constraints — schema filter, min/max
      if (pType === 'nodes') {
        if (param.schema) p.schema = String(param.schema);
        if (param.min !== undefined) p.min = Number(param.min);
        if (param.max !== undefined) p.max = Number(param.max);
      }

      inputParameters.push(p);
    });
  }

  // Field restrictions (schema_grants)
  let schemaGrants = null;
  if (parsed.field_restrictions && typeof parsed.field_restrictions === 'object') {
    schemaGrants = {};
    for (const [uri, grant] of Object.entries(parsed.field_restrictions)) {
      if (!grant || typeof grant !== 'object') {
        errors.push(`field_restrictions["${uri}"]: must be an object with optional "fields" and "filter".`);
        continue;
      }
      const entry = {};
      if (grant.fields) {
        if (!Array.isArray(grant.fields)) {
          errors.push(`field_restrictions["${uri}"].fields: must be an array of field names.`);
        } else {
          entry.fields = grant.fields.map(String);
        }
      }
      if (grant.filter && typeof grant.filter === 'object') {
        entry.filter = grant.filter;
      }
      schemaGrants[uri] = entry;
    }
  }

  // Parse top-level profile requirement
  let profileRequirement = null;
  if (parsed.profile !== undefined) {
    const pv = parsed.profile;
    if (pv === 'required' || pv === true) {
      profileRequirement = 'required';
    } else if (pv === 'optional') {
      profileRequirement = 'optional';
    } else if (pv === false) {
      profileRequirement = null;
    } else {
      errors.push('"profile" must be "required", "optional", true, or false.');
    }
  }

  return { dlrInputs, inputParameters, schemaGrants, profileRequirement, errors };
};

/**
 * Generate human-readable YAML from a Data Lender Request (DLR) object.
 * Works for both app-generated DLR (via buildDLRFromContract) and plugin DLR.
 *
 * @param {Object} dlr - The DLR object
 * @param {Object} opts - Options
 * @param {Object} opts.schemaNameMap - Map of schema ID → name
 * @param {Object} opts.pluginNameMap - Map of plugin ID → name
 * @param {Array}  opts.dlrInputs - The unified DLR inputs/requests array
 * @param {Array}  opts.inputParameters - The raw input_parameters array
 * @returns {string} YAML string
 */
const generateDLRYaml = (dlr, opts = {}) => {
  const {
    schemaNameMap = {},
    pluginNameMap = {},
    dlrInputs = [],
    inputParameters = [],
    schemaGrants = null,
  } = opts;

  const resolveName = (id, map) => map[String(id)] || id;
  const lines = [];

  lines.push('# Data Lender Request (DLR)');
  lines.push('# Auto-generated from request declarations');
  lines.push('');

  // --- Profile requirement ---
  const profileReq = opts.profileRequirement || (dlr && dlr.profile_requirement);
  if (profileReq) {
    lines.push(`profile: ${profileReq}`);
    lines.push('');
  }

  // --- Requests (unified: data + permissions) ---
  if (dlrInputs.length > 0) {
    lines.push('requests:');
    for (const req of dlrInputs) {
      const rt = requestTypeOf(req) || 'nodes:read';
      const [resource, verb] = rt.includes(':') ? rt.split(':') : [rt, undefined];
      lines.push(`  - key: ${req.key || '(unnamed)'}`);
      lines.push(`    resource: ${req.resource || resource}`);
      if (req.verb || verb) lines.push(`    verb: ${req.verb || verb}`);
      if (req.label) lines.push(`    label: "${req.label}"`);
      if (req.description) lines.push(`    description: "${req.description}"`);

      // Node scope: schemas present = those schema types; absent = any node.
      const hasNodeFilter = ['nodes:read', 'nodes:edit', 'nodes:create', 'nodes:delete', 'nodes:propose'].includes(rt);
      if (hasNodeFilter) {
        const sids = req.schema_ids || req.schemas || (req.schema_id ? [req.schema_id] : []);
        if (sids.length > 0) {
          lines.push(`    schemas: [${sids.map(id => resolveName(id, schemaNameMap)).join(', ')}]`);
        }
        if (rt !== 'nodes:create') {
          lines.push(`    min: ${req.min ?? 0}`);
          lines.push(`    max: ${req.max === null || req.max === undefined ? 'unlimited' : req.max}`);
        }
        if (req.required !== undefined) lines.push(`    required: ${req.required}`);
      }

      if (rt === 'tags:read') {
        lines.push('    visibility: some  # user selects during auth');
      }

      if (rt === 'plugins:execute') {
        if (req.plugin_ids?.length > 0) {
          lines.push('    plugins:');
          req.plugin_ids.forEach(id => lines.push(`      - ${resolveName(id, pluginNameMap)}`));
        } else {
          lines.push('    plugins: all');
        }
      }

      // A canonical discovery item says what it asks in its verb; the flags are
      // the legacy flags-only spelling and would read as extra asks beside it.
      if (rt === 'discovery' && !req.verb) {
        lines.push(`    friends: ${req.friends ?? true}`);
        lines.push(`    groups: ${req.groups ?? true}`);
        if (req.realms) lines.push(`    realms: ${req.realms}`);
      }
    }
    lines.push('');
  }

  // --- Variables (user fills these at auth time) ---
  if (inputParameters.length > 0) {
    lines.push('variables:');
    for (const param of inputParameters) {
      lines.push(`  - key: ${param.key || '(unnamed)'}`);
      lines.push(`    type: ${param.type || 'text'}`);
      if (param.label) lines.push(`    label: "${param.label}"`);
      if (param.required !== undefined) lines.push(`    required: ${param.required}`);
      if (param.type === 'select' && param.options?.length > 0) {
        lines.push(`    options: [${param.options.join(', ')}]`);
      }
      if (param.type === 'file' && param.accept) {
        lines.push(`    accept: "${param.accept}"`);
      }
      // nodes constraints
      if (param.type === 'nodes') {
        if (param.schema) lines.push(`    schema: ${param.schema}`);
        if (param.min !== undefined) lines.push(`    min: ${param.min}`);
        if (param.max !== undefined) lines.push(`    max: ${param.max}`);
      }
    }
    lines.push('');
  }

  // --- Field Restrictions (schema_grants) ---
  if (schemaGrants && typeof schemaGrants === 'object' && Object.keys(schemaGrants).length > 0) {
    lines.push('field_restrictions:');
    for (const [uri, grant] of Object.entries(schemaGrants)) {
      if (!grant || typeof grant !== 'object') continue;
      lines.push(`  ${uri}:`);
      if (grant.fields?.length > 0) {
        lines.push(`    fields: [${grant.fields.join(', ')}]`);
      }
      if (grant.filter && Object.keys(grant.filter).length > 0) {
        lines.push('    filter:');
        for (const [field, pred] of Object.entries(grant.filter)) {
          if (!pred || typeof pred !== 'object') continue;
          lines.push(`      ${field}:`);
          for (const [op, val] of Object.entries(pred)) {
            lines.push(`        ${op}: ${JSON.stringify(val)}`);
          }
        }
      }
    }
    lines.push('');
  }

  // --- Derived Permissions (auto-computed from requests) ---
  const rv = dlr.requested_verbs || dlr;

  lines.push('# --- Derived permissions ---');
  lines.push('');

  const resources = ['nodes', 'tags', 'plugins', 'discovery', 'activity', 'automations', 'sub_entity'];
  for (const resource of resources) {
    const verbs = Array.isArray(rv[resource]) ? rv[resource] : [];
    if (verbs.length > 0) {
      lines.push(`${resource}:`);
      verbs.forEach(v => lines.push(`  - ${v}`));
      lines.push('');
    }
  }

  return lines.join('\n').trimEnd();
};

/**
 * Build a DLR-like object from plugin form state (for YAML preview).
 * Uses deriveVerbsFromRequests for consistent verb derivation.
 */
const buildPluginDLR = (pluginForm) => {
  const inputs = pluginForm.dlr_inputs || pluginForm.node_requirements || [];
  return {
    requested_verbs: deriveVerbsFromRequests(inputs),
    dlr_inputs: inputs,
  };
};

// Verb (resource.verb) -> canonical request-type key. (Previously mapped to legacy clause names;
// re-keyed to canonical 'resource:verb' keys as part of the canonical DLR migration. NOTE: the
// emitted preview YAML now uses canonical keys; redesigning the YAML output is a separate task.)
const VERB_TO_KEY = {
  'nodes.read': 'nodes:read', 'nodes.create': 'nodes:create', 'nodes.edit_content': 'nodes:edit',
  'nodes.delete': 'nodes:delete', 'nodes.propose': 'nodes:propose',
  'tags.discover': 'tags:discover', 'tags.read': 'tags:read', 'tags.create': 'tags:create',
  'tags.delete': 'tags:delete', 'tags.assign': 'tags:assign',
  'plugins.execute': 'plugins:execute', 'activity.read': 'activity:read',
  'automations.write': 'automations:write', 'automations.execute': 'automations:execute',
  'sub_entity.declare': 'sub_entity:declare',
};

/**
 * Generate canonical YAML for the auth callback preview.
 * Uses flat verb clauses (matching Realm/EP format) instead of the old custom format.
 */
const generateAuthPreviewYaml = ({
  entityName = '',
  realmName = '',
  profileName = '',
  expiresLabel = '',
  enabledVerbs = {},
  scopeFilters = {},
  selectedSchemas = [],
  selectedTags = [],
  schemaGrants = null,
  schemaNameMap = {},
  tagNameMap = {},
}) => {
  const lines = [];

  // Context
  lines.push(`entity: "${entityName}"`);
  lines.push(`realm: "${realmName}"`);
  if (profileName) lines.push(`profile: "${profileName}"`);
  if (expiresLabel) lines.push(`duration: "${expiresLabel}"`);
  lines.push('');

  // Flat verb clauses
  const resolveName = (id, map) => map[String(id)] || id;

  for (const [resource, verbs] of Object.entries(enabledVerbs)) {
    if (!Array.isArray(verbs) || verbs.length === 0) continue;
    if (resource === 'discovery') {
      const disc = {};
      if (verbs.includes('friends')) disc.friends = true;
      if (verbs.includes('groups')) disc.groups = true;
      if (verbs.includes('realms')) disc.realms = true;
      lines.push(`discover:`);
      if (disc.friends !== undefined) lines.push(`  friends: ${disc.friends}`);
      if (disc.groups !== undefined) lines.push(`  groups: ${disc.groups}`);
      if (disc.realms !== undefined) lines.push(`  realms: ${disc.realms}`);
      continue;
    }
    for (const verb of verbs) {
      const clauseKey = VERB_TO_KEY[`${resource}.${verb}`];
      if (!clauseKey) continue;
      // Realm scope_filters are still keyed by the realm scope-clause name; resolve via the
      // registry's scopeFilterKey so narrowing survives the canonical re-key.
      const sfKey = REQUEST_TYPE_REGISTRY[clauseKey]?.scopeFilterKey || clauseKey;
      const sf = scopeFilters[sfKey] || {};
      const parts = [];
      // Resolve scope filter values to clean names
      if (sf.schema_ids?.length > 0) {
        parts.push(`  schemas: [${sf.schema_ids.map(id => resolveName(id, schemaNameMap)).join(', ')}]`);
      }
      if (sf.tag_ids?.length > 0) {
        parts.push(`  tags: [${sf.tag_ids.map(id => resolveName(id, tagNameMap)).join(', ')}]`);
      }
      if (sf.visibility) parts.push(`  visibility: ${sf.visibility}`);
      if (sf.friends !== undefined) parts.push(`  friends: ${sf.friends}`);
      if (sf.groups !== undefined) parts.push(`  groups: ${sf.groups}`);

      if (parts.length > 0) {
        lines.push(`${clauseKey}:`);
        parts.forEach(p => lines.push(p));
      } else {
        lines.push(`${clauseKey}: all`);
      }
    }
  }
  lines.push('');

  // Data selections
  if (selectedSchemas.length > 0) {
    lines.push(`selected_schemas: [${selectedSchemas.map(id => resolveName(id, schemaNameMap)).join(', ')}]`);
  }
  if (selectedTags.length > 0) {
    lines.push(`selected_tags: [${selectedTags.map(id => resolveName(id, tagNameMap)).join(', ')}]`);
  }

  // Field restrictions
  if (schemaGrants && typeof schemaGrants === 'object' && Object.keys(schemaGrants).length > 0) {
    lines.push('');
    lines.push('field_restrictions:');
    for (const [uri, grant] of Object.entries(schemaGrants)) {
      if (!grant || typeof grant !== 'object') continue;
      lines.push(`  ${uri}:`);
      if (grant.fields?.length > 0) {
        lines.push(`    fields: [${grant.fields.join(', ')}]`);
      }
      if (grant.filter && Object.keys(grant.filter).length > 0) {
        lines.push('    filter:');
        for (const [field, pred] of Object.entries(grant.filter)) {
          if (!pred || typeof pred !== 'object') continue;
          lines.push(`      ${field}:`);
          for (const [op, val] of Object.entries(pred)) {
            lines.push(`        ${op}: ${JSON.stringify(val)}`);
          }
        }
      }
    }
  }

  return lines.join('\n').trimEnd();
};

export { generateDLRYaml, buildPluginDLR, parseDLRYaml, generateAuthPreviewYaml };
export default generateDLRYaml;
